/**
 * The word filter's lists (docs/SAFETY.md §4, S1 phase 6). Severe slurs and sexual terms are refused
 * in character names and chat: this stops the obvious cases, and reports and the stewards handle the
 * rest. General swearing (`swearing`) is refused in names and starred out in chat.
 *
 * Entries are lower-case letters. The matcher (game-core `words.ts`) folds look-alike characters
 * and lets any letter repeat, so list each word once, as it is spelt. A trailing `*` matches any
 * word that starts with it; otherwise a word matches as written or with a plural `s` or `es`.
 *
 * Leave off words with an innocent meaning a player of a medieval game might use: "chink" (of
 * coin, in armour), "dyke" (a ditch), "nonce" (for the nonce), "dick" (Dick Whittington), "cock".
 */
export const blocklist = {
  /** Refused anywhere inside a name, and as a whole word in chat. */
  anywhere: [
    'nigger*',
    'nigga',
    'niggaz',
    'faggot*',
    'tranny',
    'trannies',
    'shemale*',
    'raghead*',
    'towelhead*',
    'wetback*',
    'golliwog*',
    'paedophil*',
    'pedophil*',
    'cunt*',
    'whore*',
    'slut*',
    'porn*',
    'hentai',
    'blowjob*',
    'handjob*',
    'rimjob*',
    'cumshot*',
    'dildo*',
    'jizz*',
    'bukkake',
    'gangbang*',
    'deepthroat*',
    'fellatio',
    'cunnilingus',
    'masturbat*',
  ],
  /**
   * Too short, or too often found inside innocent words and surnames (Fagan, Wogan, Spicer,
   * Pakington), to match within one. Refused only as a whole word, in names as in chat.
   */
  wordOnly: [
    'fag',
    'kike',
    'spic',
    'wog',
    'paki',
    'gook',
    'coon',
    'beaner',
    'retard',
    'retarded',
    'paedo',
    'pedo',
    'cum',
    'wank*',
  ],
  /**
   * General swearing: refused in names as above (`anywhere` inside one, `wordOnly` as a whole word),
   * starred out of chat as a whole word ("s***"). Left off for their medieval meaning: "bastard"
   * (the sword, the son), "ass" (the beast), "prick" (of a needle), "knob", "shag" (tobacco).
   */
  swearing: {
    anywhere: [
      'fuck*',
      'motherfuck*',
      'clusterfuck*',
      'bullshit*',
      'dipshit*',
      'horseshit*',
      'batshit',
      'apeshit',
      'arsehole*',
      'asshole*',
      'dumbass*',
      'bollock*',
      'dickhead*',
      'knobhead*',
      'shithead*',
      'tosser*',
      'wanker*',
    ],
    /** Inside innocent words and names (Ashitaka, Arsenal, Atwater, Bellenden, Pissarro), so whole words only. */
    wordOnly: ['shit*', 'bitch*', 'twat*', 'arse', 'piss', 'pissed', 'bellend', 'crap', 'feck*', 'fck*', 'fuk', 'fukin', 'fukken', 'stfu', 'wtf'],
  },
} as const;
